import DataGrid, { FormatterProps, EditorProps, RowsChangeData } from 'react-data-grid'
import { PicklistExtraColumn, PicklistItemExtraChoiceColumn } from '../../../constants/Column'
import { MdPostAdd } from 'react-icons/md'
import { useEffect, useState, memo, useContext } from 'react'
import { DgtPdOrderPicklist, ExtraReturnStatus, DgtPdOrderPicklistExtraReturn, DgtPdOrderPicklistExtraReturnItem, DgtPdMovementReason } from "../../../models"
import usePicklistExtra from '../../../hooks/usePicklistExtra'
import Modal from '../../../components/Modal'
import { useForm, SubmitHandler, useFieldArray, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { ExtraForm, OrderPicklistExtra, PicklistMetaData } from '../../../components/types'
import { 
  getRemotePicklistById,
  pdPostExtraPicklist,
  approveExtraPicklist,
  cancelExtraReturnPicklist,
  createExtraDIWMovement
} from '../../../states/PicklistAction'
import CheckboxFormatter from '../../../formatters/CheckboxFormatter'
import Badge from '../../../components/Badge'
import NoRowFallback from '../../../components/NoRowFallback'
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai'

import Select from 'react-select';

import { DataStore, SortDirection } from '@aws-amplify/datastore'

import { MovementType } from '../../../constants/EnumType'
import PicklistContext from '../../../contexts/PicklistContext'

const ConfirmApproveDialog = memo(({ props, username, isApprove = true } : {
  props: EditorProps<DgtPdOrderPicklistExtraReturn>, username: string, isApprove: boolean
}) => {
  const { onClose, onRowChange, row } = props
  
  const items: PicklistMetaData[] = row.items ? JSON.parse(row.items) : []
  return isApprove ? <Modal show={true} onHide={onClose}
    okTheme='success'
    okText='อนุมัติ'
    okAction={() => {
      const updatedRow = { ...row,
        approvedDateTime: Math.floor(Date.now() / 1000),
        approvedBy: username
      }
      onRowChange(updatedRow, true)
    }}
    cancelText='ปิด'
    title={`กำลังจะอนุมัติใบเบิกเพิ่มของ ORDER#${row.orderId}`}
    maxWidth='max-w-xl'
    >
      <>
        <h6>รายการเบิก {items.length} รายการ</h6>
        <div className='space-y-2'>
          {items.map(itm => <p key={itm.materialCode} className='text-base'>
          &#8226; {itm.materialCode} - {itm.materialDescription} = <span className='font-bold '>{itm.qty} {itm.materialUnit}</span>
          </p>)}
        </div>
      </>
    </Modal> :
    <Modal show={true} onHide={onClose}
      okTheme='danger'
      okText='ยกเลิก'
      okAction={() => {
        const updatedRow = { ...row,
          canceledDateTime: Math.floor(Date.now() / 1000),
          canceledBy: username
        }
        onRowChange(updatedRow, true)
      }}
      cancelText='ปิด'
      title={`กำลังจะยกเลิกใบเบิกเพิ่มของ ORDER#${row.orderId}`}
      maxWidth='max-w-xl'
    >
      <>
        <h6>รายการเบิก {items.length} รายการ</h6>
        <div className='space-y-2'>
          {items.map(itm => <p key={itm.materialCode} className='text-base'>
          &#8226; {itm.materialCode} - {itm.materialDescription} = <span className='font-bold '>{itm.qty} {itm.materialUnit}</span>
          </p>)}
        </div>
      </>
    </Modal>
})

export default function ExtraCompoent({ orderNo = '', username, status, dateRange, keyword, division, orderType = '', readOnly = false } : {
  orderNo?: string, username: string,
  status: ExtraReturnStatus, dateRange: [Date, Date], keyword: string,
  division: string,
  orderType?: string,
  readOnly?: boolean
}) {
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [selectedRows, setSelectedRows] = useState<ReadonlySet<string>>(() => new Set());
  const [items, setItems] = useState<DgtPdOrderPicklist[]>([]);
  const [header, setHeader] = useState<DgtPdOrderPicklist>();
  const [movementReasons, setMovementReasons] = useState<DgtPdMovementReason[]>([]);
  
  const schema = yup.object({
    orderId: yup.string().required('กรุณากรอก ORDER NO.'),
    extraItems: yup.array().of(
      yup.object().shape({
        requestQty: yup.number().moreThan(0, 'กรอกจำนวน')
      })
    ).test({
      message: 'กรุณากรอกจำนวนขอเบิก',
      test: arr => (arr?.filter(mv => mv.requestQty! > 0) || []).length > 0
    }),
    reasonCode: yup.string().required('กรุณาเลือกเหตุผล'),
    remark: yup.string().nullable()
  }).required();

  const { 
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    control, watch, reset
  } = useForm<ExtraForm>({
    defaultValues: {
      orderId: orderNo,
      extraItems: []
    },
    resolver: yupResolver(schema)
  })

  const { replace } = useFieldArray<ExtraForm>({
    control, name: 'extraItems'
  })

  const watchOrderId = watch('orderId')

  const [ picklistExtras, mutateExtra ] = usePicklistExtra(
    status, orderType,
    keyword, division, orderNo,
    dateRange ? dateRange[0]: null, dateRange ? dateRange[1]: null)

  const picklistContext = useContext(PicklistContext)
  
  useEffect(() => {
    if (picklistContext && picklistContext?.items.length > 0) {
      setHeader(picklistContext.header)
      
      const clearQty = picklistContext?.items.filter(itm => itm.materialType !== 'BULK')
        .map(itm => ({ ...itm, requestQty: 0 }))
      setItems(clearQty)
      return
    }
    if (watchOrderId.length === 8) {
      const fetchItems = async (id :string) => {
        const { header, items } = await getRemotePicklistById(id)
        const clearQty = items.filter(itm => itm.materialType !== 'BULK')
          .map(itm => ({ ...itm, requestQty: 0 }))
        setHeader(header)
        setItems(clearQty)
      }
      fetchItems(watchOrderId)
    }
  }, [ watchOrderId, picklistContext ])

  const onRowsChange = (rows: DgtPdOrderPicklist[]) => {
    setItems(rows)
    replace(rows.filter(row => row.requestQty! > 0))
  }

  const onClosedForm = () => {
    // TODO - Clear amount
    setSelectedRows(() => new Set())
    setOpenModal(false)
  }

  const addDIWMovement = async (changedRow: DgtPdOrderPicklistExtraReturn) => {
    const extraItems = JSON.parse(changedRow.items || '') as DgtPdOrderPicklistExtraReturnItem[]
    if (extraItems && extraItems.length > 0 && extraItems.find(itm => itm.materialCode === '118003')) {
      const orderInfo = JSON.parse(changedRow.orderInfo || '') as DgtPdOrderPicklist
      const divItem = extraItems.find(itm => itm.materialCode === '118003')
      await createExtraDIWMovement(changedRow.orderId, changedRow.id, orderInfo.division!, divItem?.qty || 0)
    }
  }

  const onSubmitForm: SubmitHandler<ExtraForm> = async (data) => {
    const remarks: string[] = []
    if (data.reasonCode) remarks.push(data.reasonCode)
    if (data.remark) remarks.push(data.remark)

    if (status) {
      let tempExtraReturnStatus = ExtraReturnStatus.APPROVED
      if (data.extraItems.length === 1) {
        const tempMaterialCodeFirst = data.extraItems[0]
        const DIW = '118003';
        if (tempMaterialCodeFirst?.materialCode === DIW){
          // warehouse skip approve if MaterialCodeFirst = DIW Only
          tempExtraReturnStatus = ExtraReturnStatus.QA_CHECKED
        }
      }
      const inserted = await pdPostExtraPicklist(header!, data.extraItems, remarks.join(' / '), username, tempExtraReturnStatus)
      await addDIWMovement(inserted) // note : support DIW movement in case of new extra by teamleader
      if (inserted) mutateExtra(inserted)
    } else {
      const inserted = await pdPostExtraPicklist(header!, data.extraItems, remarks.join(' / '), username)
      if (inserted) mutateExtra(inserted)
    }
    setOpenModal(false)
  }

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset()
    }
  }, [ reset, isSubmitSuccessful ])

  const onLeaderApprovedOrCanceled = async (rows: DgtPdOrderPicklistExtraReturn[],
    changedInfo: RowsChangeData<DgtPdOrderPicklistExtraReturn>) => {
    const changedRowIdx = changedInfo.indexes[0]
    if (changedRowIdx < 0) return
    
    const changedRow = rows[changedRowIdx] as OrderPicklistExtra
    if (changedRow.approvedBy && changedRow.approvedDateTime! > 0) {
      const updated = await approveExtraPicklist(changedRow, username)
      await addDIWMovement(changedRow)
      mutateExtra(updated)
    } else if (changedRow.canceledBy && changedRow.canceledDateTime! > 0) {
      const updated = await cancelExtraReturnPicklist(changedRow, username)
      mutateExtra(updated)
    }
  }

  const onAddNewComponent = () => {
    const currentItems = [...items]
    setItems([...currentItems, { 
      materialCode: '', materialType: '', sk: '',
      pk: '', id: `${Math.floor(Date.now() / 1000)}`,
      materialUnit: '', materialDescription: '',
      requestQty: 0, externalBom: true
    }])
  }

  let columns = Array.from(PicklistExtraColumn)
  if (!orderNo) {
    columns.splice(0, 0, { key: 'orderId', name: 'Order ID', width: 90 });
    if (!readOnly) {
      columns = [...columns, 
        { 
          key: 'approval', name: 'อนุมัติ', width: 100, formatter: ({ row }: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
            return <div className='flex items-center justify-center h-full'>
              {row.approvedDateTime || row.canceledDateTime ? 
                <div>-</div> : 
                <h6 className={`flex items-center gap-1 work-center-edit text-green-600`}><AiOutlineCheck /> อนุมัติ</h6>
              }
            </div>
          }, editor(props: EditorProps<DgtPdOrderPicklistExtraReturn>) {
            return <ConfirmApproveDialog props={props} username={username} isApprove={true}/>
          }, editorOptions: {
            editOnClick: true, commitOnOutsideClick: true
          }, 
        },
        { 
          key: 'cancel', name: 'ยกเลิก', width: 100, formatter: ({ row }: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
            return <div className='flex items-center justify-center h-full'>
              {row.approvedDateTime || row.canceledDateTime ? 
                <div>-</div> : 
                <h6 className={`flex items-center gap-1 work-center-edit text-red-600`}><AiOutlineClose /> ยกเลิก</h6>
              }
            </div>
          }, editor(props: EditorProps<DgtPdOrderPicklistExtraReturn>) {
            return <ConfirmApproveDialog props={props} username={username} isApprove={false}/>
          }, editorOptions: {
            editOnClick: true, commitOnOutsideClick: true
          }
        }
      ]
    }
  }
  
  const badgeStatus = (status: ExtraReturnStatus) => {
    let badgeColor = 'bg-red-500' // for NEW
    if (status === ExtraReturnStatus.APPROVED) badgeColor = 'bg-green-500'
    else if (status === ExtraReturnStatus.WITHDRAWED) badgeColor = 'bg-green-500'
    else if (status === ExtraReturnStatus.CHECKED) badgeColor = 'bg-green-700'
    else if (status === ExtraReturnStatus.CANCELED) badgeColor = 'bg-gray-900'

    return <Badge variant={`${badgeColor} text-xs text-white`}>{status}</Badge>
  }

  
  useEffect(() => {
    if (!openModal) return

    const getMovementReasons = async (ordNo: string) => {
      const mvReas = await DataStore.query(
        DgtPdMovementReason, 
        s => s
          .movementType("eq", MovementType.ISSUE)
          .componenType('eq', ordNo.startsWith('8') ? 'RM':'PK')
          .active('eq', true), 
          {
            sort: s => s.reasonCode(SortDirection.ASCENDING)
          }
        )
      
      setMovementReasons(mvReas)
    }

    if (orderNo) {
      getMovementReasons(orderNo)
    } else if (watchOrderId.length === 8) {
      getMovementReasons(watchOrderId)
    }
  }
  , [ openModal, orderNo, watchOrderId ])

  return <div className='py-3'>
    <div className='flex items-center justify-between py-2'>
      <h6>รายการเบิกเพิ่ม <span className={`${status ? '':'hidden '}font-bold`}>{badgeStatus(status)}</span> {picklistExtras.length} รายการ</h6>
      {readOnly ? null : <button className='gap-1 btn-primary' onClick={() => setOpenModal(true)}><MdPostAdd />เปิดใบเบิกเพิ่ม</button>}
    </div>
    <DataGrid
      className='mb-3 '
      style={{ height: "100%", contain: "none" }}
      columns={columns}
      rows={picklistExtras}
      onRowsChange={onLeaderApprovedOrCanceled}
      defaultColumnOptions={{ resizable: true }}
      rowHeight={row => {
        if (row.type === 'GROUP') return 35
        const items = row.row.items ? JSON.parse(row.row.items) : []
        return (items.length === 0 ? 1 : items.length) * 50
      }}
      components={{ noRowsFallback: <NoRowFallback title='ขอเบิกเพิ่ม'/>}}
    />
    <Modal
      show={openModal} title='เปิดคำร้องขอเบิกเพิ่ม'
      onHide={() => setOpenModal(false)}
      maxWidth='max-w-3xl'
      >
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className='w-48 mb-3 space-y-1'>
          <label className='inline-block'>PD Order#</label>
          <input type="text" placeholder="กรอก PD ORDER"
            className={`input-form-text ${errors.orderId ? ' border-red-500':''}`} {...register('orderId')}
          />
          {errors.orderId ? <small className='text-red-500'>{errors.orderId.message}</small> : null}
        </div>
        <h6>เลือก Component ที่ต้องการเบิกเพิ่ม</h6>
        <DataGrid
          className='mb-3 '
          style={{ height: 'fit-content' }}
          columns={ PicklistItemExtraChoiceColumn }
          rows={items}
          defaultColumnOptions={{ resizable: true }}
          onRowsChange={onRowsChange}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          rowKeyGetter={row => row.id}
          components={{ checkboxFormatter: CheckboxFormatter }}
          rowClass={row => row.externalBom ? ' bg-green-100':''}
        />
        <button type='button' className='float-right mb-2 btn-outline-success'
          onClick={onAddNewComponent} disabled={items.length === 0}>เพิ่มนอก BOM</button>
        <div className='py-5 space-y-3'>
          <div>
            <label className='inline-block mt-2'>เลือกสาเหตุ</label>
            <Controller
              name="reasonCode"
              control={control}
              render={({ field }) => 
                <Select
                  options={movementReasons.map(res => (
                    {
                      label: `${res.reasonCode} - ${res.reasonDesciption}`, 
                      value: res.reasonCode
                    }
                  ))}
                  menuPortalTarget={document.body}
                  styles={{ 
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                  placeholder='เลือกเหตุผล...'
                  onChange={(e: any) => field.onChange(e.label)}
                />
              }
            />
            {errors.reasonCode ? <small className='text-red-500'>{errors.reasonCode.message}</small> : null}
          </div>
          <div>
            <label className='inline-block mt-2'>พิมพ์สาเหตุอื่นๆ</label>
            <input type="text" placeholder="พิมพ์สาเหตุ นอกเหนือจากที่ตัวเลือกไม่มี"
              className={`input-form-text`} {...register('remark')}
            />
          </div>
        </div>
        {errors.extraItems ? <small className='text-red-500'>{JSON.stringify(errors.extraItems)}</small> : null}
        <button type='submit' className="float-right ml-3 btn-primary" disabled={selectedRows.size === 0}>
          {status ? 'ส่งเรื่อง/อนุมัติเบิก' : 'ส่งเรื่องขอเบิก'}
        </button>
        <button type='button' className="float-right btn-outline-primary" onClick={onClosedForm}>
          ปิด
        </button>
      </form>
    </Modal>
  </div>
}